import { useMemo } from "react";
import {
  Box,
  Button,
  Callout,
  Flex,
  IconButton,
  Popover,
  ScrollArea,
  Text,
} from "@radix-ui/themes";
import {
  Cross2Icon,
  InfoCircledIcon,
  QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import Animal from "react-animals";
import { useGondolaChangeGroupsData } from "../hooks/useGondolaChangeGroupsData";
import { detectTracksWithMultipleJourneys } from "../lib/helpers";

export function AvatarTrackJourneyAssociationPopover() {
  const { journeyTrackRelations, trackIdsInOcclusion, trackAvatarBindings } =
    useGondolaChangeGroupsData();

  const tracksWithMultipleJourneys = useMemo(() => {
    return detectTracksWithMultipleJourneys(journeyTrackRelations);
  }, [journeyTrackRelations]);

  const multipleJourneysCount = tracksWithMultipleJourneys.length;
  const getMultipleJourneysMessage = () => {
    const isPlural = multipleJourneysCount !== 1;
    return `${multipleJourneysCount} track${isPlural ? "s" : ""} ${isPlural ? "are" : "is"} linked to multiple journey IDs.`;
  };

  // Create table data by combining all sources
  const tableData = useMemo(() => {
    type TableRow = {
      trackId: number;
      avatar: { animalName: string; color: string };
      recoveredTrackIds: number[];
      journeyId: string | null;
    };

    const rows: TableRow[] = [];

    // Add journey track relations (green avatars)
    journeyTrackRelations.forEach((relation) => {
      relation.trackIds.forEach((trackId) => {
        const binding = trackAvatarBindings[trackId];
        if (binding) {
          rows.push({
            trackId,
            avatar: {
              animalName: binding.animalName,
              color: binding.color,
            },
            recoveredTrackIds: relation.recoveredTrackIds.filter(
              (id) => id !== trackId
            ),
            journeyId: relation.journeyId,
          });
        }
      });
    });

    // Add track IDs in occlusion (red avatars)
    trackIdsInOcclusion.forEach((trackId) => {
      const binding = trackAvatarBindings[trackId];
      if (binding) {
        rows.push({
          trackId,
          avatar: {
            animalName: binding.animalName,
            color: binding.color,
          },
          recoveredTrackIds: [],
          journeyId: null,
        });
      }
    });

    return rows.sort((a, b) => {
      // First, sort by journey status (tracks with journeys first)
      if (a.journeyId && !b.journeyId) return -1;
      if (!a.journeyId && b.journeyId) return 1;

      // Then sort by trackId within each group
      return a.trackId - b.trackId;
    });
  }, [
    journeyTrackRelations,
    trackIdsInOcclusion,
    trackAvatarBindings,
  ]) as Array<{
    trackId: number;
    avatar: { animalName: string; color: string };
    recoveredTrackIds: number[];
    journeyId: string | null;
  }>;

  return (
    <Popover.Root>
      <Popover.Trigger>
        <IconButton
          size="1"
          variant={multipleJourneysCount > 0 ? "soft" : "ghost"}
          color={multipleJourneysCount > 0 ? "red" : "gray"}
          mr="2"
        >
          <QuestionMarkCircledIcon />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content width="810px" minWidth="810px">
        <Flex direction="column" gap="3">
          {/* Header */}
          <Flex justify="between" align="center">
            <Text size="4" weight="bold">
              Avatar • Track • Journey Association
            </Text>
            <Popover.Close>
              <Button variant="ghost" color="gray" size="1">
                <Cross2Icon />
              </Button>
            </Popover.Close>
          </Flex>

          <ScrollArea scrollbars="vertical" className="h-[500px]">
            <Flex direction="column" gap="2">
              {/* Color Legend */}
              <Flex direction="column" gap="2" className="bg-gray-100 p-2">
                <Flex align="center" gap="2">
                  <div className="w-4 h-4 rounded-full bg-red-500" />
                  <Text size="2">Red avatar background - Journey missing</Text>
                </Flex>
                <Flex align="center" gap="2">
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <Text size="2">Green avatar background - Journey exists</Text>
                </Flex>
                <Flex align="center" gap="2">
                  <div className="w-4 h-4 rounded-full bg-yellow-500" />
                  <Text size="2">
                    Yellow background - Tracks that don’t have relation with
                    SSCGs
                  </Text>
                </Flex>
              </Flex>

              {/* Multiple Journeys Callout */}
              {multipleJourneysCount > 0 && (
                <Callout.Root color="red">
                  <Callout.Icon>
                    <InfoCircledIcon />
                  </Callout.Icon>
                  <Callout.Text>
                    <Text size="2">{getMultipleJourneysMessage()}</Text>
                  </Callout.Text>
                </Callout.Root>
              )}

              {/* Info Callout */}
              <Callout.Root color="amber">
                <Callout.Icon>
                  <InfoCircledIcon />
                </Callout.Icon>
                <Callout.Text>
                  <Text size="2">
                    The "Track ID" column displays the track detected near the
                    Journey's exit point, if it is linked to a Journey.
                  </Text>
                  <br />
                  <br />
                  <Text size="2">
                    The track that isn't associated with any Journey, might
                    likely belong to a non-Journey individual. In such cases,
                    multiple avatars may represent the same person due to track
                    fragmentation.
                  </Text>
                </Callout.Text>
              </Callout.Root>

              {/* Table */}
              <Box>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="px-1 py-1 text-center font-medium text-gray-900">
                        Avatar
                      </th>

                      <th className="px-1 py-1 text-left font-medium text-gray-900">
                        Track ID
                      </th>
                      <th className="px-1 py-1 text-left font-medium text-gray-900">
                        Recovered Track IDs
                      </th>
                      <th className="px-1 py-1 text-left font-medium text-gray-900">
                        Creation Time
                      </th>
                      <th className="px-1 py-1 text-left font-medium text-gray-900">
                        Journey ID
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row) => (
                      <tr
                        key={row.trackId}
                        className={`hover:bg-gray-100 ${
                          !row.journeyId ? "bg-red-50" : ""
                        }`}
                      >
                        <td className="px-1 py-1">
                          <div className="flex justify-center items-center">
                            <Animal
                              name={row.avatar.animalName}
                              size="20px"
                              color={row.avatar.color}
                            />
                          </div>
                        </td>

                        <td className="px-1 py-1 font-mono text-xs">
                          {row.trackId}
                        </td>
                        <td className="px-1 py-1 font-mono text-xs max-w-0">
                          <div className="truncate">
                            {row.recoveredTrackIds.length > 0
                              ? row.recoveredTrackIds.join(", ")
                              : "-"}
                          </div>
                        </td>
                        <td className="px-1 py-1 font-mono text-xs">
                          {new Date(row.trackId / 1000).toISOString()}
                        </td>
                        <td className="px-1 py-1 font-mono text-xs">
                          {row.journeyId || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {/* Summary */}
              <Box>
                <Text size="1" color="gray">
                  Total: {tableData.length} tracks (
                  {tableData.filter((row) => row.journeyId).length} with
                  journey, {tableData.filter((row) => !row.journeyId).length}{" "}
                  without journey)
                </Text>
              </Box>
            </Flex>
          </ScrollArea>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
